import React, { useCallback, useEffect, useState } from 'react';
import { LifeBuoy } from 'lucide-react';
import { closeMyTicket, getMyTicket, listMyTickets, replyToMyTicket, type Ticket } from '../../api/supportApi';
import { LifecycleBadge, TicketHistory, fmtWhen } from './TicketThread';

/**
 * The customer's support tickets (SRS #17): status, full history, reply,
 * close. New tickets are still raised from the Support button.
 */
const MySupportPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [open, setOpen] = useState<Ticket | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    listMyTickets().then((r) => setTickets(r.tickets)).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const show = async (id: number) => {
    setError(null);
    try { setOpen(await getMyTicket(id)); setDraft(''); } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load ticket'); }
  };
  const act = async (fn: () => Promise<Ticket>) => {
    setBusy(true); setError(null);
    try { const t = await fn(); setOpen(t); setDraft(''); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const text = 'var(--color-text, var(--ds-text))';
  const border = 'var(--ds-border, var(--ds-border))';

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '28px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <LifeBuoy size={22} aria-hidden />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: text }}>My Support Requests</h1>
      </div>
      <p style={{ margin: '0 0 20px', color: muted, fontSize: 14 }}>
        Track each request from open to closed, reply to our team, and close a request once it is sorted. Use the Support button (bottom-right) to raise a new one.
      </p>

      {loading && <p style={{ color: muted }}>Loading…</p>}
      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 14, marginBottom: 12 }}>{error} <button onClick={load} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button></div>}
      {!loading && !error && tickets.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: muted }}>You haven't sent any support requests yet.</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: open ? 'minmax(min(240px, 100%), 1fr) 2fr' : '1fr', gap: 18 }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tickets.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => show(t.id)}
                      style={{ width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10, border: `1px solid ${open?.id === t.id ? 'var(--ds-brand-blue)' : border}`, background: 'var(--ds-surface, #fff)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <strong style={{ fontSize: 14, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>#{t.id} · {t.subject}</strong>
                  <LifecycleBadge value={t.lifecycle} />
                </div>
                <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>Updated {fmtWhen(t.updated_at || t.created_at)}</div>
              </button>
            </li>
          ))}
        </ul>

        {open && (
          <section style={{ border: `1px solid ${border}`, borderRadius: 12, padding: 16, background: 'var(--ds-surface, #fff)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 17, color: text }}>#{open.id} · {open.subject}</h2>
                <div style={{ fontSize: 12, color: muted, marginTop: 3 }}>Opened {fmtWhen(open.created_at)}{open.attachment_name ? ` · attachment: ${open.attachment_name}` : ''}</div>
              </div>
              <LifecycleBadge value={open.lifecycle} />
            </div>
            <TicketHistory ticket={open} viewer="customer" />
            {open.lifecycle !== 'closed' ? (
              <form style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
                    onSubmit={(e) => { e.preventDefault(); if (draft.trim()) act(() => replyToMyTicket(open.id, draft)); }}>
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder={open.lifecycle === 'resolved' ? 'Not resolved after all? Reply and the ticket reopens.' : 'Add to this request…'}
                          style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 8, border: `1px solid ${border}`, font: 'inherit', fontSize: 13.5 }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" disabled={busy} onClick={() => { if (window.confirm('Close this request? You can open a new one any time.')) act(() => closeMyTicket(open.id)); }}
                          style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', cursor: 'pointer', fontSize: 13 }}>
                    {open.lifecycle === 'resolved' ? 'Yes, this is resolved — close' : 'Close request'}
                  </button>
                  <button type="submit" disabled={busy || !draft.trim()} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>Send reply</button>
                </div>
              </form>
            ) : (
              <p style={{ marginTop: 14, fontSize: 13, color: muted }}>This request is closed. Use the Support button to open a new one.</p>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default MySupportPage;
